import { useEffect, useState } from 'react';
import type { PaymentMode } from '@dunea/shared';
import { apiGetPaymentsOverview } from '~/lib/api/payments';
import { apiListOrders, type Order } from '~/lib/api/shop';
import { classNames } from '~/utils/classNames';
import {
  CARD,
  LoadError,
  Pill,
  Section,
  Segmented,
  Select,
  SkeletonCard,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { AdminActionsMenu, useSlotEntries } from '~/components/workbench/admin-extensions';
import { bulkResourceId } from '~/components/workbench/admin-extensions/admin-extensions-model';
import { OrderDrawer } from './OrderDrawer';
import { buyerLabel, formatDate, formatMinor, ORDER_STATUS_FILTERS, orderStatusMeta } from './shop-model';

export function OrdersView({ appId }: { appId: string }) {
  const [mode, setMode] = useState<PaymentMode>('test');
  const [modeTouched, setModeTouched] = useState(false);
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const statuses = ORDER_STATUS_FILTERS.find(([id]) => id === filter)?.[2];
  const orders = useResource(
    () => apiListOrders(appId, { mode, status: statuses, limit: 100 }),
    `${appId}:${mode}:${filter}`,
  );

  // Selection for bulk admin actions; shown only when an action exists.
  const bulkEnabled = useSlotEntries(appId, 'shop.orders.bulk-actions').length > 0;
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => setSelected([]), [appId, mode, filter]);

  // Once payments are live, real orders are what the owner wants first.
  const payments = useResource(() => apiGetPaymentsOverview(appId), appId);
  const liveState = payments.data?.settings.liveState;

  useEffect(() => {
    if (!modeTouched && liveState && liveState !== 'test') {
      setMode('live');
    }
  }, [liveState, modeTouched]);

  const modeSwitch = (
    <Segmented
      label="Order mode"
      value={mode}
      options={[
        ['test', 'Test'],
        ['live', 'Live'],
      ]}
      onChange={(m) => {
        setModeTouched(true);
        setMode(m);
      }}
    />
  );

  return (
    <Section title="Orders" icon="i-ph:receipt" aside={modeSwitch}>
      <Select value={filter} onChange={setFilter} label="Filter orders by status" className="w-48">
        {ORDER_STATUS_FILTERS.map(([id, label]) => (
          <option key={id} value={id}>
            {label}
          </option>
        ))}
      </Select>

      {orders.loading && !orders.data ? (
        <SkeletonCard lines={3} />
      ) : orders.error ? (
        <LoadError message="Could not load orders." onRetry={() => void orders.reload()} />
      ) : !orders.data?.length ? (
        <div className={classNames(CARD, 'px-4 py-6 text-center')}>
          <div className="i-ph:receipt mx-auto mb-2 text-xl text-faint" />
          <p className="text-[13px] text-primary">
            No {mode} orders{filter === 'all' ? ' yet' : ' here'}
          </p>
          <p className="mx-auto mt-1 max-w-[260px] text-[12px] leading-relaxed text-muted">
            {mode === 'test'
              ? 'Place an order from the preview to try checkout. Test orders never touch stock.'
              : 'Orders from your published site appear here once payments are live.'}
          </p>
        </div>
      ) : (
        <>
          {bulkEnabled && (
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-[12px] text-muted">
                <input
                  type="checkbox"
                  className="accent-accent"
                  checked={selected.length > 0 && selected.length === orders.data.length}
                  onChange={(e) => setSelected(e.target.checked ? orders.data!.map((o) => o.id) : [])}
                  aria-label="Select all orders"
                />
                {selected.length ? `${selected.length} selected` : 'Select'}
              </label>
              <div className="flex-1" />
              <AdminActionsMenu
                appId={appId}
                target="shop.orders.bulk-actions"
                resourceId={bulkResourceId(selected)}
                label="Bulk actions"
                disabledHint="Select orders first (up to 100)"
              />
            </div>
          )}
          <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
            {orders.data.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                onOpen={() => setOpenId(order.id)}
                selected={bulkEnabled ? selected.includes(order.id) : undefined}
                onSelect={(on) =>
                  setSelected((prev) => (on ? [...prev, order.id] : prev.filter((id) => id !== order.id)))
                }
              />
            ))}
          </div>
        </>
      )}

      {openId && (
        <OrderDrawer
          appId={appId}
          orderId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => void orders.reload()}
        />
      )}
    </Section>
  );
}

function OrderRow({
  order,
  onOpen,
  selected,
  onSelect,
}: {
  order: Order;
  onOpen: () => void;
  /** undefined = no selection UI (no bulk actions installed). */
  selected?: boolean;
  onSelect?: (on: boolean) => void;
}) {
  const status = orderStatusMeta(order.status);
  const row = (
    <button
      type="button"
      onClick={onOpen}
      className={classNames(
        'flex w-full min-w-0 items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.02]',
        selected !== undefined && 'pl-2',
        status.attention && 'bg-danger/[0.04]',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-geist-mono text-[13px] text-primary">#{order.number}</span>
          <Pill tone={status.tone} label={status.label} />
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted">
          <span className="truncate text-secondary">{buyerLabel(order)}</span>
          <span aria-hidden>·</span>
          <span>{formatDate(order.createdAt)}</span>
        </div>
      </div>
      <span className="shrink-0 font-geist-mono text-[13px] text-primary">
        {formatMinor(order.total, order.currency)}
      </span>
      <div className="i-ph:caret-right shrink-0 text-[12px] text-faint" />
    </button>
  );

  if (selected === undefined) {
    return row;
  }

  return (
    <div className="flex items-center">
      <input
        type="checkbox"
        className="accent-accent ml-4 shrink-0"
        checked={selected}
        onChange={(e) => onSelect?.(e.target.checked)}
        aria-label={`Select order #${order.number}`}
      />
      {row}
    </div>
  );
}
